/**
 * Hear it: a score engraved on a desk, with a transport under it, on the page that made it.
 *
 * This is the point of the whole board, and the board mounts two of them -- the same bars as
 * they were written and as the rules re-wrote them -- because the answer to "arrange this for
 * a quartet" is not a file, it is a sound, and a sound is judged against another sound. It is
 * judged against it one after the other, so a board that mounts two hands each panel a seat
 * on a stage only one of them holds; see :file:`../lib/use-stage.ts`.
 *
 * The panel is laid out the way a notation program lays out a document: a title strip with
 * what can be done to the score, the page itself on a ground, and one bar along the bottom
 * for where the music is, how fast it goes and how big it is drawn. Nothing that belongs to
 * the music is written in prose here; the prose under the transport is about the reading.
 *
 * The score arrives already parsed, because `usePlayback` and `useRendering` both want a score
 * and a hook cannot be skipped when the parse fails — the caller decides whether there is
 * anything to audition, and this panel only exists when there is.
 *
 * The audio context is made by the click that starts the music and not before, because a
 * browser refuses one until the user has asked for sound. It is a sampled engine, so each
 * part is heard on a recording of its own instrument -- without that, comparing two panels
 * by ear compares two rows of oscillators and the whole board is pointless. The recordings
 * are fetched after that first click, which is why play can pulse for a moment before the
 * music starts: the press is held, not refused.
 *
 * Speed is a setting of the playing and not of the score. The panel plays a copy whose tempo
 * marks are multiplied and engraves the original, so slowing a piece down to hear the inner
 * parts never changes what is printed or what is downloaded -- the MusicXML, or the MP3 and
 * WAV that play the score into a file on the same instruments (`ScoreDownloads`). At 1x
 * nothing is copied at all and the score plays at the speed it is marked: the source's own,
 * carried through the arranger, which is why the two panels can be compared by ear in the
 * first place.
 *
 * The desk holds one engraved page at a time, turned by the pager in the transport, and the
 * music is followed on it the way a notation program follows it: a line crosses the bar as it
 * sounds, a click on the page moves the music there -- playing on if it was playing -- the
 * desk scrolls to the next system when the music reaches it, and the page turns when the
 * music runs onto the next one. See :file:`../lib/use-playhead.ts` and
 * :file:`../lib/use-shown-page.ts`. The page opens fitted whole to the desk, so it is read
 * without scrolling, and the desk goes full screen with its transport; the space bar plays
 * and pauses it (:file:`../lib/use-fullscreen.ts`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { NotationEngine, Score } from "@app/score-editor";
import { ScoreView, usePlayback, useRendering } from "@app/score-editor/react";
import { ACTION } from "./Chrome";
import ScoreCanvas, { SHEET } from "./ScoreCanvas";
import ScoreDownloads from "./ScoreDownloads";
import type { Zoom } from "./ScoreCanvas";
import ScoreTransport from "./ScoreTransport";
import { notationEngine } from "../lib/verovio";
import { FILLED, useFullscreen, useSpaceToPlay } from "../lib/use-fullscreen";
import { usePlayhead } from "../lib/use-playhead";
import { barOf, useShownPage } from "../lib/use-shown-page";
import { useSampledEngine, useSamplesFor } from "../lib/use-sampled-engine";
import type { Stage } from "../lib/use-stage";

export interface ScoreAuditionProps {
  readonly score: Score;
  /**
   * The score the audio downloads play, where the desk draws fewer bars than it has: the
   * file sent to someone is the whole piece, not the part of it on the page. Left out, it is
   * `score`.
   */
  readonly sound?: Score;
  readonly musicxml: string;
  readonly fileName: string;
  /** What this panel is playing, since the board shows two of them side by side. */
  readonly heading: string;
  /** The sentence under the transport, if this panel has something to say about its sound. */
  readonly note?: string;
  /**
   * This panel's seat on the board, when the board mounts more than one of them. Pressing
   * play takes the stage and the panel that held it stops, because two versions of the same
   * bars sounding together is not a comparison. Left out, the panel plays on its own.
   */
  readonly stage?: Stage;
  /** Whether the space bar plays this panel, the board having more than one. */
  readonly spaceKey?: boolean;
  /** Left out when the score is the caller's own upload and the editor would add nothing. */
  onOpenInEditor?: (() => void) | undefined;
  /** Hands the score to Arrange, on a board that only reads one; left out everywhere else. */
  onArrange?: (() => void) | undefined;
}

/** Quarter notes per minute a player picks where the score states no tempo at all. */
const UNMARKED = 120;

/** How loud the panel plays, 0 to 1, and so how loud the file it saves is. */
const VOLUME = 0.5;

/** How the audio downloads play the score: as loud as the panel, at the speed it is marked. */
const MARKED = { volume: VOLUME, tempo: UNMARKED } as const;

/** The same score with every tempo mark multiplied, which is a copy only when it has to be. */
function atRate(score: Score, rate: number): Score {
  if (rate === 1) return score;
  return {
    ...score,
    measures: score.measures.map((measure) =>
      measure.tempo === null
        ? measure
        : { ...measure, tempo: { ...measure.tempo, bpm: measure.tempo.bpm * rate } },
    ),
  };
}

/** The speed the score is marked at, which is the first mark in it, or none. */
function markedAt(score: Score): number | null {
  return score.measures.find((measure) => measure.tempo !== null)?.tempo?.bpm ?? null;
}

export default function ScoreAudition({
  score,
  sound = score,
  musicxml,
  fileName,
  heading,
  note,
  stage,
  spaceKey = true,
  onOpenInEditor,
  onArrange,
}: ScoreAuditionProps): JSX.Element {
  const [engine, setEngine] = useState<NotationEngine | null>(null);
  const [engineError, setEngineError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    notationEngine().then(
      (loaded) => {
        if (live) setEngine(loaded);
      },
      (reason: unknown) => {
        if (live) setEngineError(reason instanceof Error ? reason.message : String(reason));
      },
    );
    return () => {
      live = false;
    };
  }, []);

  const { rendering, error } = useRendering(engine, score, { scale: 34 });

  const { engine: audio, wake } = useSampledEngine();
  const [rate, setRate] = useState(1);
  const [zoom, setZoom] = useState<Zoom>("page");
  const [resolvedZoom, setResolvedZoom] = useState(1);
  const played = useMemo(() => atRate(score, rate), [score, rate]);
  const playback = usePlayback(audio, played, { volume: VOLUME, tempo: UNMARKED * rate });
  const recorded = useSamplesFor(audio, playback.schedule);
  const playhead = usePlayhead(played, playback);
  const shown = useShownPage(rendering, [barOf(played, playhead.at)]);
  const started = useRef(false);

  // Changing the speed is a new schedule, and a new schedule stops the player it belongs to.
  // Where the music was going, it is put back on at the same place in the piece, which is a
  // different number of seconds in now that the seconds are a different length.
  const resume = useRef<number | null>(null);
  const change = useCallback(
    (next: number) => {
      resume.current = playback.playing ? (playback.position * rate) / next : null;
      setRate(next);
    },
    [playback, rate],
  );
  useEffect(() => {
    const seconds = resume.current;
    resume.current = null;
    if (seconds !== null) playback.play(seconds);
  }, [playback, rate]);

  // The order to stop, which arrives as another panel taking the stage. It is watched here
  // rather than pushed by the taker because a panel's player is its own and nothing outside
  // it holds one.
  useEffect(() => {
    if (stage !== undefined && !stage.held && playback.playing) playback.pause();
  }, [stage, playback]);

  const toggle = useCallback(() => {
    if (playback.playing) {
      playback.pause();
      return;
    }
    stage?.take();
    wake();
    // Held, not refused: the press is remembered and acted on as soon as the instruments
    // this score is scored for have been fetched, which is once and only on the first play.
    if (audio === null || !recorded) {
      started.current = true;
      return;
    }
    // From the position rather than from wherever the player stopped: a click on the page
    // before the first play moved the position while there was no player yet to move.
    playback.play(playback.position);
  }, [audio, playback, recorded, stage, wake]);

  const desk = useRef<HTMLElement>(null);
  const fullscreen = useFullscreen(desk);
  useSpaceToPlay(desk, spaceKey, toggle);

  useEffect(() => {
    if (audio === null || !recorded || !started.current) return;
    started.current = false;
    playback.play(playback.position);
  }, [audio, playback, recorded]);

  return (
    <section
      ref={desk}
      className={`flex min-h-[20rem] flex-1 flex-col overflow-hidden bg-shell-bar ${
        fullscreen.on ? FILLED : "rounded-md border border-shell-edge shadow-chrome"
      }`}
    >
      <header className="flex shrink-0 items-center gap-2 border-b border-shell-edge px-2.5 py-1.5">
        <h2 className="truncate text-[12px] font-medium text-shell-lit">{heading}</h2>
        <ScoreDownloads sound={sound} options={MARKED} musicxml={musicxml} fileName={fileName} />
        {onOpenInEditor === undefined ? null : (
          <button type="button" onClick={onOpenInEditor} className={ACTION}>
            Open in editor
          </button>
        )}
        {onArrange === undefined ? null : (
          <button type="button" onClick={onArrange} className={ACTION}>
            Arrange this
          </button>
        )}
      </header>

      {engineError !== null || error !== null || rendering === null ? (
        <div className="flex min-h-0 flex-1 items-center justify-center bg-canvas p-8 text-center">
          <p
            className={`font-mono text-[12px] ${
              engineError === null && error === null ? "text-shell-dim" : "text-fault"
            }`}
          >
            {engineError !== null
              ? `The engraver did not load: ${engineError}`
              : (error ?? "Engraving…")}
          </p>
        </div>
      ) : (
        <ScoreCanvas
          zoom={zoom}
          onResolved={setResolvedZoom}
          following={playback.playing}
          sheet={shown.page}
        >
          <ScoreView
            key={shown.page}
            page={shown.page}
            className={SHEET}
            rendering={rendering}
            score={score}
            sounding={playback.sounding}
            playhead={playhead.at}
            onPick={playhead.jump}
          />
        </ScoreCanvas>
      )}

      <ScoreTransport
        playing={playback.playing}
        loading={audio !== null && !recorded}
        position={playback.position}
        duration={playback.schedule.seconds}
        onToggle={toggle}
        onStop={playback.stop}
        onSeek={playback.seek}
        rate={rate}
        onRate={change}
        tempo={markedAt(score)}
        zoom={zoom}
        resolvedZoom={resolvedZoom}
        onZoom={setZoom}
        page={shown.page}
        pages={shown.count}
        onPage={shown.turn}
        fullscreen={fullscreen}
      />

      {note === undefined || fullscreen.on ? null : (
        <p className="shrink-0 border-t border-shell-edge px-2.5 py-1.5 text-[11px] leading-relaxed text-shell-dim">
          {note}
        </p>
      )}
    </section>
  );
}
